import {
  ComboBox,
  DirectionalHint,
  Dropdown,
  DropdownMenuItemType,
  IconButton,
  IDropdownOption,
  IDropdownProps,
  IDropdownStyles,
  SearchBox,
} from '@fluentui/react';
import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import './SearchableDD.css';
import { UATConstants } from 'src/Common/Models/UATConstants';
import { GetDropdownOptions } from '../Models/DropdownConstants';
import { TooltipHost } from '@fluentui/react/lib/Tooltip';

export interface SearchProps {
  selectedItem: any;
  dropdownOptions: IDropdownOption[];
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  onChangeCallBack: (option?: IDropdownOption) => void;
  onSearchChange?: (searchText: string) => void;
  tooltipDirectionalHint?: DirectionalHint;
  // Opt-in "X" affordance that resets the selection to empty (single-select only).
  clearable?: boolean;
  onClear?: () => void;
}

export const SearchableDD: React.FunctionComponent<SearchProps> = (props) => {
  // Keep the clear-affordance props out of the `{...dropdownProps}` spread so they never leak onto the
  // underlying Fluent `Dropdown` (which does not understand them).
  const { clearable, onClear, ...dropdownProps } = props;
  const [searchText, setSearchText] = React.useState<any>('');
  const [hoveredTooltip, setHoveredTooltip] = React.useState<{ text: string; top: number; left: number } | null>(null);
  const searchInputRef = useRef<SearchBox>(null);
  let focusTimeout: NodeJS.Timeout;

  useEffect(() => {
    return () => {
      if (focusTimeout) {
        clearTimeout(focusTimeout);
      }
    };
  }, []);

  const dropdownStyles: Partial<IDropdownStyles> = {
    dropdownItemHeader: {
      position: 'fixed',
      zIndex: 2,
      width: '100%',
      margin: '0px',
      marginLeft: '-7px',
      marginTop: '-4vh',
    },
    dropdownItems: {
      overflow: 'hidden',
      position: 'relative',
    },
    dropdownItem: {
      position: 'relative',
      whiteSpace: 'nowrap',
      wordBreak: 'normal',
    },
    callout: {
      maxHeight: 'none !important',
      backgroundColor: 'white !important',
      selectors: {
        '.ms-Callout-main': {
          overflow: 'hidden',
          marginTop: '4vh',
        },
      },
    },
  };

  function renderOption(option?: IDropdownOption): JSX.Element {
    if (option && option.itemType === DropdownMenuItemType.Header && option.key === 'FilterHeader') {
      // Use timeout to ensure focus after the SearchBox renders
      focusTimeout = setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 500);

      return (
        <SearchBox
          title="Search"
          autoFocus
          componentRef={searchInputRef}
          onChange={(ev, newValue) => {
            if (props.onSearchChange) {
              props.onSearchChange(newValue ?? '');
            } else {
              setSearchText(newValue ?? '');
            }
          }}
          onFocus={() => {
            if (props.onSearchChange) {
              props.onSearchChange('');
            }
          }}
          tabIndex={0}
          placeholder={UATConstants.PlaceholdersAndTitles.SearchOptions}
        />
      );
    }

    if (props.tooltipDirectionalHint !== undefined) {
      const tooltipContent = option?.data?.fullText || option?.text;

      return (
        <div
          style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}
          onMouseEnter={(e) => {
            const el = e.currentTarget;
            if (el.scrollWidth > el.clientWidth) {
              const rect = el.getBoundingClientRect();
              setHoveredTooltip({
                text: tooltipContent || '',
                top: rect.top + rect.height / 2,
                left: rect.right + 8,
              });
            }
          }}
          onMouseLeave={() => setHoveredTooltip(null)}
        >
          {option?.text}
        </div>
      );
    }

    return (
      <div title={option?.disabled ? option?.title : undefined}>{option?.text}</div>
    );
  }

  function setDropItem(option?: IDropdownOption): void {
    if (option) {
      props.onChangeCallBack(option);
    }
  }

  // Ensure dropdownOptions is never undefined to prevent map errors
  const safeDropdownOptions = props.dropdownOptions || [];

  return (
    <TooltipHost content={props.tooltipDirectionalHint !== undefined ? '' : props.label}>
      <div className='newSearchableDropdown' style={{ position: 'relative' }}>
        <Dropdown
          {...dropdownProps}
          title='Search'
          options={[
            {
              key: 'FilterHeader',
              text: '-',
              itemType: DropdownMenuItemType.Header,
              title: "Search",
            },
            {
              key: 'divider_filterHeader',
              text: '-',
              itemType: DropdownMenuItemType.Divider,
              title: "Divider",
            },
            ...safeDropdownOptions.map((option) =>
              option?.text?.toLowerCase().indexOf(searchText?.toLowerCase()) > -1
                ? { ...option, disabled: option.disabled }
                : { ...option, hidden: true }
            ),
          ]}
          calloutProps={{
            shouldRestoreFocus: true,
            setInitialFocus: true,
          }}
          onRenderOption={renderOption}
          onDismiss={() => { setSearchText(''); setHoveredTooltip(null); }}
          styles={dropdownStyles}
          onChange={(_, option) => setDropItem(option)}
          selectedKey={props.selectedItem}
          disabled={props.disabled}
        />
        {clearable && props.selectedItem && !props.disabled && (
          <IconButton
            iconProps={{ iconName: 'Cancel' }}
            title='Clear'
            ariaLabel='Clear selection'
            onMouseDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onClear?.();
            }}
            styles={{
              root: {
                position: 'absolute',
                right: 28,
                top: '50%',
                transform: 'translateY(-50%)',
                height: 24,
                width: 24,
                zIndex: 10,
              },
              icon: { fontSize: 12 },
            }}
          />
        )}
      </div>
      {hoveredTooltip && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed',
            top: hoveredTooltip.top,
            left: hoveredTooltip.left,
            transform: 'translateY(-50%)',
            background: '#242424',
            color: '#ffffff',
            padding: '8px 12px',
            borderRadius: '8px',
            maxWidth: '400px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            zIndex: 1000001,
            pointerEvents: 'none',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            fontSize: '14px',
            lineHeight: '1.4',
          }}
        >
          <div
            style={{
              position: 'absolute',
              left: '-6px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: 0,
              height: 0,
              borderTop: '6px solid transparent',
              borderBottom: '6px solid transparent',
              borderRight: '6px solid #242424',
            }}
          />
          {hoveredTooltip.text}
        </div>,
        document.body
      )}
    </TooltipHost>
  );
};
// Generated by GitHub Copilot | Updated on: 2026-05-15 | 2 lines modified | Removed redundant initializeIcons() call to minimize dynamic style injection
